<template>
  <div
    class="relative z-10 flex w-[90%] items-center justify-evenly"
    :class="getContainerClasses(title)"
  >
    <div class="relative z-0 flex h-full w-full items-center justify-evenly">
      <div
        v-for="player in modifiedPlayers"
        :key="player.playerIndex"
        :class="getPlayerClasses(player, formation, locale)"
        @click="selectCard(player)"
        class="group"
      >
        <div
          v-if="player.playerCaps || player.playerWorldCups"
          class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:flex flex-col items-center"
        >
          <div
            class="whitespace-nowrap rounded-lg bg-black text-white text-xs p-2 shadow-lg"
          >
            <div v-if="locale === 'ar'">
              {{ $t("caps") }}: {{ player.playerCaps }} <br />
              {{ formatWorldCups(player.playerWorldCups) }} :{{
                $t("appearances")
              }}
            </div>
            <div v-else>
              {{ $t("caps") }}: {{ player.playerCaps }} <br />
              {{ $t("appearances") }}:
              {{ formatWorldCups(player.playerWorldCups) }}
            </div>
          </div>
        </div>
        <div
          class="absolute bottom-[75%] left-[-0.6vw] z-[100] flex h-[6vmin] w-[6vmin] rotate-45 items-center justify-center rounded-lg border-2 border-slate-100 bg-zinc-900 text-[2.5vw] md:h-[4vmin] md:w-[4vmin] md:text-[1.5vw]"
          v-if="Number(newCapId) === Number(player.playerId)"
        >
          <h1
            :class="{
              'text-[1vw] md:text-[0.4vw]': locale === 'ar',
              'text-[3vw] md:text-[1.6vw]': locale !== 'ar',
            }"
            class="mr-[2%] rotate-[-45deg] text-white"
          >
            {{ locale === "ar" ? "قائد الفريق" : "C" }}
          </h1>
        </div>

        <div class="flex h-full w-full">
          <img
            :src="spottyBackground"
            alt="Background"
            class="relative z-[0] h-full w-full object-fill md:h-[full] md:w-[full]"
          />
          <img
            :class="player.playerImage === blankPlayerImage ? 'mt-[5%]' : ''"
            class="absolute h-full w-full rounded-full object-fill md:h-full md:w-full"
            :src="player.playerImage || blankPlayerImage"
            alt="Player Image"
          />
          <div
            v-if="!player?.playerImage"
            class="absolute inset-0 z-50 mb-[10%] flex items-center justify-center text-[4vw] text-white transition-colors hover:text-slate-400"
          >
            +
          </div>
        </div>
        <div
          v-if="player?.playerName !== ''"
          class="absolute top-[90%] z-[100] h-[3vh] flex w-[14vw] items-center justify-center rounded-lg border-[1px] border-saudi-purple bg-saudi-dark text-[1.5vw] text-white md:w-[5.75vw] md:text-[0.65vw]"
        >
          {{ capitaliseName(player.playerName) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { watch, ref } from "vue";
import blankPlayerImage from "../assets/blankPlayer.png";
import spottyBackground from "../assets/spottyBackground.png";

const props = defineProps({
  players: {
    type: Array,
    required: true,
  },
  formation: {
    type: String,
    required: false,
  },
  title: {
    type: String,
    required: false,
  },
  captainId: {
    type: Number,
    required: false,
  },
  locale: {
    type: String,
    required: false,
  },
});

const modifiedPlayers = ref([...props.players]);

const checkFormation = () => {
  if (props.formation === "451 2" && modifiedPlayers.value.length === 5) {
    modifiedPlayers.value[2].position =
      props.locale !== "ar" ? "CAM" : "صانع لعب";
    modifiedPlayers.value[1].position =
      props.locale !== "ar" ? "CDM" : "محور دفاعي";
    modifiedPlayers.value[3].position =
      props.locale !== "ar" ? "CDM" : "محور دفاعي";
  }
  if (props.formation === "451 3" && modifiedPlayers.value.length === 5) {
    modifiedPlayers.value[2].position =
      props.locale !== "ar" ? "CDM" : "محور دفاعي";
  }

  if (props.formation === "442 2" && modifiedPlayers.value.length === 4) {
    if (
      modifiedPlayers.value.every(
        (player) => player.positionCategory === "Midfielder"
      )
    ) {
      modifiedPlayers.value[0].position = props.locale !== "ar" ? "CM" : "محور";
      modifiedPlayers.value[1].position =
        props.locale !== "ar" ? "CDM" : "محور دفاعي";
      modifiedPlayers.value[2].position = props.locale !== "ar" ? "CM" : "محور";
      modifiedPlayers.value[3].position =
        props.locale !== "ar" ? "CAM" : "صانع لعب";
    }
  }
};

watch(() => props.formation, checkFormation);

watch(() => props.players, (newPlayers) => { modifiedPlayers.value = [...newPlayers] });

const newCapId = ref(100);

watch(() => props.captainId,(newCaptainID) => { newCapId.value = newCaptainID });

const emit = defineEmits(["selectCard"]);

const selectCard = (player) => { emit("selectCard", player) };

const getContainerClasses = (title) => {
  return title === "Goalkeeper" ? "h-[0%] md:mt-[4vh] mt-[-4vh]" : "h-[20%]";
};

const getPlayerClasses = (player, formation, locale) => {
  const classes = [
    "border-2 hover:border-saudi-green transition-colors border-white cursor-pointer w-[13vw] h-[13vw] md:w-[6vw] md:h-[6vw] rounded-full flex items-center justify-center text-3xl ",
  ];

  const positions = {
    CAM: locale === "ar" ? "صانع لعب" : "CAM",
    CDM: locale === "ar" ? "محور دفاعي" : "CDM",
    CM: locale === "ar" ? "محور" : "CM",
    LWB: locale === "ar" ? "ظهير أيسر" : "LWB",
    RWB: locale === "ar" ? "ظهير أيمن" : "RWB",
    LM: locale === "ar" ? "جناح أيسر" : "LM",
    RM: locale === "ar" ? "جناح أيمن" : "RM",
  };

  if (
    formation !== "442 2" ||
    player.positionCategory !== "Midfielder" ||
    player.position === positions.CM
  ) {
    classes.push("relative flex flex-col items-center ");
  }

  if (player.position === positions.LWB || player.position === positions.RWB) {
    classes.push("mt-[14vh]");
  }

  if (player.position === positions.CAM || player.position === positions.CDM) {
    classes.push(
      player.position === positions.CAM ? "mt-[14vh]" : "mt-[-14vh]"
    );
  }

  if (formation === "451 2") {
    if (player.position === positions.LM || player.position === positions.RM) {
      classes.push("mt-[8vh]");
    }
  }

  if (formation === "442 2" && player.positionCategory === "Midfielder") {
    if (
      player.position === positions.CAM ||
      player.position === positions.CDM
    ) {
      classes.push(
        player.position === positions.CAM
          ? "mt-[20vh] absolute"
          : "mt-[-12vh] absolute"
      );
    }
  }

  return classes;
};

const capitaliseName = (name) => {
  const nameParts = name.split(" ");
  const firstNameInitial = nameParts[0].charAt(0).toUpperCase();
  const lastName = nameParts.slice(1).join(" ").toUpperCase();
  return `${firstNameInitial}.${lastName}`;
};
function formatWorldCups(worldCupsArray) {
  return worldCupsArray.map((item) => item.replace("WC", "")).join(", ");
}
</script>

<style scoped></style>
